/**
 * Telegram Mini App shim.
 *
 * Wires Telegram.WebApp into the canonical UI:
 *  - ready() + expand() so the WebApp fills the chat
 *  - theme bridge: TG palette → our --color-paper / --color-ink
 *  - MainButton: maps to whatever the active screen sets via setMainCta()
 *  - HapticFeedback: bridged on solve / commit
 */

interface TgWebApp {
  ready(): void;
  expand(): void;
  close(): void;
  MainButton: {
    setText(s: string): void;
    show(): void;
    hide(): void;
    onClick(fn: () => void): void;
    offClick(fn: () => void): void;
    enable(): void;
    disable(): void;
  };
  BackButton: {
    show(): void;
    hide(): void;
    onClick(fn: () => void): void;
    offClick(fn: () => void): void;
  };
  HapticFeedback: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'success' | 'warning' | 'error'): void;
  };
  themeParams: { bg_color?: string; text_color?: string; hint_color?: string };
  colorScheme: 'light' | 'dark';
  initData: string;
}

// `window.Telegram` is declared in platform/detect.ts as `unknown` to avoid
// pulling the full TgWebApp shape into the cold-path detection. Here we cast.

let tg: TgWebApp | null = null;
let activeMainCallback: (() => void) | null = null;

export function initTg(): void {
  tg = (window.Telegram?.WebApp as TgWebApp | undefined) ?? null;
  if (!tg) return;
  tg.ready();
  tg.expand();

  const params = tg.themeParams;
  const root = document.documentElement.style;
  if (params.bg_color) root.setProperty('--color-paper', params.bg_color);
  if (params.text_color) root.setProperty('--color-ink', params.text_color);
  if (params.hint_color) root.setProperty('--color-ink-soft', params.hint_color);

  document.documentElement.dataset['theme'] = tg.colorScheme;
}

export function setMainCta(label: string, onClick: () => void): void {
  if (!tg) return;
  if (activeMainCallback) tg.MainButton.offClick(activeMainCallback);
  activeMainCallback = onClick;
  tg.MainButton.setText(label);
  tg.MainButton.onClick(onClick);
  tg.MainButton.show();
}

export function hideMainCta(): void {
  if (!tg) return;
  if (activeMainCallback) tg.MainButton.offClick(activeMainCallback);
  activeMainCallback = null;
  tg.MainButton.hide();
}

export function tgHaptic(kind: 'light' | 'medium' | 'success'): void {
  if (!tg) return;
  if (kind === 'success') tg.HapticFeedback.notificationOccurred('success');
  else tg.HapticFeedback.impactOccurred(kind);
}
